import React, { useState, useCallback, useEffect, useRef } from "react"
import { Handle, Position } from "reactflow"
import { Clock, Minus, Plus, Trash2 } from "lucide-react"
import "./Node.css"
import "./WaitNode.css"
import { useWorkflow } from "@/lib/contexts/workflow-context"
import CopyPasteButtons from "./CopyPasteButtons"

const WAIT_UNITS = ["seconds", "minutes", "hours", "days", "business_days", "weeks", "months"]

const ensureString = (value) => (value === null || value === undefined ? "" : String(value))

const normalizeDurationExpression = (value) => {
  if (value === null || value === undefined) return "1"
  const asString = String(value).trim()
  return asString === "" ? "" : asString
}

const WaitNode = ({ data, id, csrf_token }) => {
  const { currentWorkflow, deleteNode, updateNode } = useWorkflow()
  const { expanded = true } = data

  const [isHovering, setIsHovering] = useState(false)
  const [showMinimizeTooltip, setShowMinimizeTooltip] = useState(false)
  const [waitData, setWaitData] = useState(() => ({
    label: ensureString(data.label) || "Wait",
    name: ensureString(data.name) || "Wait",
    durationUnit: WAIT_UNITS.includes(data.durationUnit) ? data.durationUnit : (WAIT_UNITS.includes(data.wait_duration_unit) ? data.wait_duration_unit : "days"),
    durationExpression: normalizeDurationExpression(data.durationExpression ?? data.wait_duration_expression ?? "1"),
    targetTime: ensureString(data.targetTime ?? data.wait_target_time) || "08:00",
    description: ensureString(data.description),
    expanded,
  }))
  const waitDataRef = useRef(waitData)

  useEffect(() => {
    const nextWaitData = {
      label: ensureString(data.label) || "Wait",
      name: ensureString(data.name) || "Wait",
      durationUnit: WAIT_UNITS.includes(data.durationUnit) ? data.durationUnit : (WAIT_UNITS.includes(data.wait_duration_unit) ? data.wait_duration_unit : "days"),
      durationExpression: normalizeDurationExpression(data.durationExpression ?? data.wait_duration_expression ?? "1"),
      targetTime: ensureString(data.targetTime ?? data.wait_target_time) || "08:00",
      description: ensureString(data.description),
      expanded: data.expanded ?? true,
    }
    setWaitData(nextWaitData)
  }, [data.label, data.name, data.durationUnit, data.durationExpression, data.wait_duration_unit, data.wait_duration_expression, data.targetTime, data.wait_target_time, data.description, data.expanded, id])

  useEffect(() => {
    waitDataRef.current = waitData
  }, [waitData])

  const updateWorkflowContext = useCallback((updatedData) => {
    if (currentWorkflow) {
      updateNode(currentWorkflow.id, id, { data: updatedData })
    }
  }, [currentWorkflow, id, updateNode])

  const handleChange = (field, value) => {
    const updated = { ...waitData, [field]: value }
    setWaitData(updated)
    // Keep canvas state in sync while editing; backend save occurs on blur.
    updateWorkflowContext(updated)
  }

  const handleBlur = () => {
    if (data.onNodeDataChange) {
      data.onNodeDataChange(id, waitDataRef.current)
    }
  }

  const toggleMinimize = () => {
    if (data.onNodeStateChange) {
      data.onNodeStateChange(id, !expanded)
    }
  }

  useEffect(() => {
    const handleWheel = (e) => {
      const nodeContent = e.target.closest(".node-content")
      if (!nodeContent) return
      if (!e.target.closest(".wait-node-outline")) return
      if (e.ctrlKey || e.shiftKey) return
      e.stopPropagation()
    }

    document.addEventListener("wheel", handleWheel, { passive: false, capture: true })
    return () => {
      document.removeEventListener("wheel", handleWheel, { capture: true })
    }
  }, [id, expanded])

  return (
    <div
      className={`trigger-node-container wait-node-outline dark:bg-card ${!expanded ? "minimized" : ""} ${isHovering ? "wait-node-hovering" : ""}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="trigger-node-header dark:bg-card dark:border-border">
        <div className="trigger-node-title">
          <Clock size={16} className="trigger-icon" />
          <span>{waitData.name && waitData.name.trim() !== "" ? waitData.name : "Wait"}</span>
        </div>
        <div className="trigger-node-controls">
          <CopyPasteButtons
            nodeId={id}
            nodeType="waitNode"
            nodeName={waitData.name}
            className="nodrag"
          />
          <div className="minimize-button-container nodrag">
            <button
              className="trigger-node-button nodrag dark:hover:bg-card dark:hover:text-white"
              onClick={(e) => {
                e.stopPropagation()
                toggleMinimize()
              }}
              onMouseEnter={() => setShowMinimizeTooltip(true)}
              onMouseLeave={() => setShowMinimizeTooltip(false)}
            >
              {!expanded ? <Plus size={16} /> : <Minus size={16} />}
            </button>
            {showMinimizeTooltip && <div className="minimize-tooltip"></div>}
          </div>
          <button
            className="trigger-node-button nodrag dark:hover:bg-card"
            onClick={async (e) => {
              e.stopPropagation()
              if (confirm("Are you sure you want to delete this node?")) {
                try {
                  if (deleteNode && currentWorkflow) {
                    await deleteNode(currentWorkflow.id, id, csrf_token)
                  } else {
                    throw new Error("Unable to delete node: missing deleteNode function or currentWorkflow")
                  }
                } catch (error) {
                  console.error("Failed to delete node:", error)
                  alert("Failed to delete node. Please try again.")
                }
              }
            }}
            title="Delete node"
            style={{ color: "#ef4444" }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="node-content dark:bg-card dark:text-white dark:border-border">
          <div className="node-field dark:text-white">
            <label className="dark:text-white">Duration units</label>
            <select
              value={waitData.durationUnit}
              onChange={(e) => handleChange("durationUnit", e.target.value)}
              onBlur={handleBlur}
              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag"
            >
              {WAIT_UNITS.map((unit) => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>
          </div>

          <div className="node-field dark:text-white">
            <label className="dark:text-white">Duration expression</label>
            <input
              type="number"
              min="1"
              step="1"
              value={waitData.durationExpression}
              onChange={(e) => handleChange("durationExpression", e.target.value)}
              onBlur={handleBlur}
              placeholder="e.g. 3"
              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag"
            />
          </div>

          <div className="node-field dark:text-white">
            <label className="dark:text-white">At</label>
            <input
              type="time"
              value={waitData.targetTime}
              onChange={(e) => handleChange("targetTime", e.target.value)}
              onBlur={handleBlur}
              className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 dark:focus:outline-none dark:focus:ring-2 dark:focus:ring-blue-500 nodrag"
            />
          </div>
        </div>
      )}

      <Handle type="target" position={Position.Top} id="b" />
      <Handle
        type="source"
        position={Position.Bottom}
        id="a"
      />
    </div>
  )
}

export default WaitNode
